import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { login, requestOtp, verifyOtp } from "../api/api";

// ─── Saffron design tokens ──────────────────────────────────────────
const SAFFRON = "#E07B1A";
const SAFFRON_DARK = "#854F0B";
const SAFFRON_BG = "#FFF8F0";
const AMBER_FILL = "#FAEEDA";
const AMBER_BORDER = "#FAC775";
const TEXT_DARK = "#412402";
const TEXT_MID = "#854F0B";
const TEXT_LABEL = "#633806";

const LOGIN_LOCK_KEY = "admin_login_lock_until";
const OTP_LOCK_KEY = "admin_otp_lock_until";

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.25rem",
    background: `radial-gradient(circle at 20% 10%, ${AMBER_FILL} 0%, ${SAFFRON_BG} 45%, ${SAFFRON_BG} 100%)`,
    position: "relative",
    overflow: "hidden",
  },
  omWatermark: {
    position: "absolute",
    fontSize: "clamp(220px, 40vw, 420px)",
    color: SAFFRON,
    opacity: 0.05,
    fontFamily: "serif",
    userSelect: "none",
    pointerEvents: "none",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    lineHeight: 1,
  },
  card: {
    position: "relative",
    zIndex: 1,
    background: "#fff",
    border: `0.5px solid ${AMBER_BORDER}`,
    boxShadow:
      "0 20px 50px rgba(133,79,11,0.16), 0 4px 14px rgba(133,79,11,0.08)",
    borderRadius: 20,
    padding: "2.5rem 1.75rem",
    width: "100%",
    maxWidth: 400,
    animation: "cardIn 0.5s cubic-bezier(0.16,1,0.3,1)",
  },
  brandIcon: {
    width: 56,
    height: 56,
    background: `linear-gradient(135deg, ${SAFFRON}, ${SAFFRON_DARK})`,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1.25rem",
    color: "#fff",
    fontSize: 26,
    boxShadow: "0 8px 20px rgba(224,123,26,0.35)",
    animation: "iconPulse 3s ease-in-out infinite",
  },
  heading: {
    textAlign: "center",
    fontSize: 21,
    fontWeight: 700,
    color: TEXT_DARK,
    marginBottom: 4,
  },
  subtitle: {
    textAlign: "center",
    fontSize: 13,
    color: TEXT_MID,
    marginBottom: "1.75rem",
  },
  field: {
    marginBottom: "1.1rem",
  },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: TEXT_MID,
    marginBottom: 5,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
  },
  inputWrap: {
    position: "relative",
  },
  input: {
    width: "100%",
    height: 48,
    padding: "0 14px",
    border: "1.5px solid",
    borderRadius: 10,
    // ── Fixes the mobile "zoom on tap" issue ──
    // This was 14px before. iOS Safari / Android Chrome auto-zoom the
    // whole page when a focused input's font-size is under 16px —
    // that's exactly what was triggering the zoom. 16px removes the
    // browser's reason to zoom at all, on any device.
    fontSize: 16,
    background: SAFFRON_BG,
    color: TEXT_DARK,
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    boxSizing: "border-box",
  },
  passwordToggle: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 18,
    color: TEXT_MID,
    padding: 6,
    WebkitTapHighlightColor: "transparent",
  },
  otpInput: {
    width: "100%",
    height: 56,
    textAlign: "center",
    fontSize: 22, // already ≥16px, was never the zoom culprit
    fontWeight: 600,
    letterSpacing: 10,
    border: "1.5px solid",
    borderRadius: 10,
    background: SAFFRON_BG,
    color: TEXT_DARK,
    outline: "none",
    fontFamily: "inherit",
    marginBottom: "1.25rem",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  btnPrimary: {
    width: "100%",
    height: 50,
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: "1.5rem",
    background: `linear-gradient(135deg, ${SAFFRON}, ${SAFFRON_DARK})`,
    color: "#fff",
    fontFamily: "inherit",
    letterSpacing: 0.3,
    WebkitTapHighlightColor: "transparent",
    transition: "transform 0.12s ease, box-shadow 0.12s ease",
    boxShadow: "0 6px 16px rgba(224,123,26,0.3)",
  },
  btnPrimaryLocked: {
    background: AMBER_FILL,
    color: TEXT_MID,
    border: `0.5px solid ${AMBER_BORDER}`,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  btnVerify: {
    flex: 1,
    height: 46,
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    background: `linear-gradient(135deg, ${SAFFRON}, ${SAFFRON_DARK})`,
    color: "#fff",
    fontFamily: "inherit",
    boxShadow: "0 4px 12px rgba(224,123,26,0.3)",
  },
  btnCancel: {
    height: 46,
    padding: "0 18px",
    border: `0.5px solid ${AMBER_BORDER}`,
    borderRadius: 8,
    background: "none",
    fontSize: 14,
    color: TEXT_MID,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  alertWarning: {
    background: AMBER_FILL,
    border: `0.5px solid ${AMBER_BORDER}`,
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: "1.25rem",
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  alertSuccess: {
    background: "#eaf3de",
    border: "0.5px solid #c0dd97",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: "1.25rem",
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
  alertIconWarning: {
    fontSize: 16,
    color: TEXT_MID,
    marginTop: 1,
    flexShrink: 0,
  },
  alertIconSuccess: {
    fontSize: 16,
    color: "#3b6d11",
    marginTop: 1,
    flexShrink: 0,
  },
  alertTitleWarning: {
    fontSize: 13,
    fontWeight: 700,
    color: TEXT_LABEL,
    marginBottom: 2,
  },
  alertTitleSuccess: {
    fontSize: 13,
    fontWeight: 700,
    color: "#27500a",
    marginBottom: 2,
  },
  alertBodyWarning: { fontSize: 12, color: TEXT_MID },
  alertBodySuccess: { fontSize: 12, color: "#3b6d11" },
  lockTimer: { fontSize: 12, fontWeight: 700, color: TEXT_LABEL, marginTop: 5 },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(65,36,2,0.45)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "1rem",
  },
  modalBox: {
    background: "#fff",
    border: `0.5px solid ${AMBER_BORDER}`,
    boxShadow: "0 24px 60px rgba(65,36,2,0.3)",
    borderRadius: 18,
    padding: "1.75rem 1.5rem",
    width: "100%",
    maxWidth: 380,
    animation: "cardIn 0.35s cubic-bezier(0.16,1,0.3,1)",
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: TEXT_DARK,
    marginBottom: 6,
  },
  modalHint: {
    fontSize: 13,
    color: TEXT_MID,
    lineHeight: 1.6,
    marginBottom: "1.25rem",
  },
  modalActions: { display: "flex", gap: 10 },
};

// ─── Alert component ──────────────────────────────────────────────
const Alert = ({ type = "warning", title, children, timerText }) => {
  const isWarning = type === "warning";
  return (
    <div
      style={isWarning ? styles.alertWarning : styles.alertSuccess}
      role="alert"
    >
      <span
        style={isWarning ? styles.alertIconWarning : styles.alertIconSuccess}
        aria-hidden="true"
      >
        {isWarning ? "⚠" : "✓"}
      </span>
      <div>
        <div
          style={
            isWarning ? styles.alertTitleWarning : styles.alertTitleSuccess
          }
        >
          {title}
        </div>
        <div
          style={isWarning ? styles.alertBodyWarning : styles.alertBodySuccess}
        >
          {children}
        </div>
        {timerText && <div style={styles.lockTimer}>🕐 {timerText}</div>}
      </div>
    </div>
  );
};

// ─── Focus-aware input (inline styles can't do :focus, so track it in state) ──
const FocusInput = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...style,
        borderColor: focused ? SAFFRON : AMBER_BORDER,
        boxShadow: focused ? `0 0 0 3px ${SAFFRON}22` : "none",
      }}
      onFocus={(e) => {
        setFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        props.onBlur?.(e);
      }}
    />
  );
};

const formatLockTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `Access resumes in ${m}m ${s}s`;
};

// ─── Main component ─────────────────────────────────────────────────
const AdminLogin = () => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");

  const [authMessage, setAuthMessage] = useState("");
  const [authMessageType, setAuthMessageType] = useState("warning");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [loginLockUntil, setLoginLockUntil] = useState(() => {
    const v = Number(localStorage.getItem(LOGIN_LOCK_KEY));
    return v && v > Date.now() ? v : null;
  });
  const [otpLockUntil, setOtpLockUntil] = useState(() => {
    const v = Number(localStorage.getItem(OTP_LOCK_KEY));
    return v && v > Date.now() ? v : null;
  });
  const [now, setNow] = useState(Date.now());

  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!loginLockUntil && !otpLockUntil) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [loginLockUntil, otpLockUntil]);

  const isLoginLocked = !!loginLockUntil && loginLockUntil > now;
  const isOtpLocked = !!otpLockUntil && otpLockUntil > now;
  const loginRemainingSeconds = isLoginLocked
    ? Math.ceil((loginLockUntil - now) / 1000)
    : 0;
  const otpRemainingSeconds = isOtpLocked
    ? Math.ceil((otpLockUntil - now) / 1000)
    : 0;

  useEffect(() => {
    if (loginLockUntil && loginLockUntil <= now) {
      localStorage.removeItem(LOGIN_LOCK_KEY);
      setLoginLockUntil(null);
    }
  }, [loginLockUntil, now]);

  useEffect(() => {
    if (otpLockUntil && otpLockUntil <= now) {
      localStorage.removeItem(OTP_LOCK_KEY);
      setOtpLockUntil(null);
    }
  }, [otpLockUntil, now]);

  const applyLoginLock = useCallback((seconds) => {
    const until = Date.now() + seconds * 1000;
    localStorage.setItem(LOGIN_LOCK_KEY, String(until));
    setLoginLockUntil(until);
  }, []);

  const applyOtpLock = useCallback((seconds) => {
    const until = Date.now() + seconds * 1000;
    localStorage.setItem(OTP_LOCK_KEY, String(until));
    setOtpLockUntil(until);
  }, []);

  const handleAuthenticate = async () => {
    if (isLoginLocked) return;
    setIsAuthenticating(true);
    setAuthMessage("");

    try {
      await login(mobile, password);
      await requestOtp(mobile);

      setAuthMessageType("success");
      setAuthMessage(
        "A verification code has been sent to your registered email address.",
      );
      setIsModalOpen(true);
    } catch (error) {
      const responseData = error?.response?.data;
      const message =
        typeof responseData === "string"
          ? responseData
          : responseData?.message || "Authentication failed. Please try again.";

      setAuthMessageType("warning");
      setAuthMessage(message);

      if (error?.response?.status === 423) {
        applyLoginLock(responseData?.remainingSeconds || 900);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleOtpVerification = async () => {
    if (isOtpLocked) return;
    setIsVerifyingOtp(true);
    setAuthMessage("");

    try {
      const response = await verifyOtp(mobile, otp);
      localStorage.setItem("token", response.data.token);
      setIsModalOpen(false);
      navigate("/admin/upload");
    } catch (error) {
      const responseData = error?.response?.data;
      const message =
        typeof responseData === "string"
          ? responseData
          : responseData?.message || "OTP verification failed.";

      setAuthMessageType("warning");
      setAuthMessage(message);

      if (error?.response?.status === 423) {
        applyOtpLock(responseData?.remainingSeconds || 900);
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .admin-btn-primary:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(224,123,26,0.4); }
        .admin-btn-primary:not(:disabled):active { transform: translateY(0); }
        .admin-btn-verify:not(:disabled):hover { filter: brightness(1.06); }
        .admin-btn-cancel:hover { background: ${AMBER_FILL}; }
        .admin-password-toggle:hover { color: ${SAFFRON}; }

        /* Extra safety net: some mobile browsers still consider zoom
           based on the viewport meta tag rather than input font-size
           alone. This keeps pinch-zoom fully available for
           accessibility while telling the browser this page is
           already sized correctly, reducing the chance of an
           unwanted auto-zoom-in on input focus. */
        @viewport { width: device-width; zoom: 1.0; }
      `}</style>

      <div style={styles.page}>
        <div style={styles.omWatermark} aria-hidden="true">
          ॐ
        </div>

        <div style={styles.card}>
          <div style={styles.brandIcon} aria-hidden="true">
            🔒
          </div>

          <h1 style={styles.heading}>Admin portal</h1>
          <p style={styles.subtitle}>Secure access · Two-factor required</p>

          {authMessage && (
            <Alert
              type={authMessageType}
              title={
                authMessageType === "warning"
                  ? "Security notification"
                  : "Verification code sent"
              }
              timerText={
                isLoginLocked ? formatLockTime(loginRemainingSeconds) : null
              }
            >
              {authMessage}
            </Alert>
          )}

          <div style={styles.field}>
            <label htmlFor="mobile" style={styles.label}>
              Mobile number
            </label>
            <FocusInput
              id="mobile"
              style={styles.input}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="Enter your mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              disabled={isLoginLocked}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="password" style={styles.label}>
              Password
            </label>
            <div style={styles.inputWrap}>
              <FocusInput
                id="password"
                style={{ ...styles.input, paddingRight: 46 }}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoginLocked}
                onKeyDown={(e) =>
                  e.key === "Enter" && !isLoginLocked && handleAuthenticate()
                }
              />
              <button
                type="button"
                className="admin-password-toggle"
                style={styles.passwordToggle}
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          <button
            className="admin-btn-primary"
            style={{
              ...styles.btnPrimary,
              ...(isLoginLocked ? styles.btnPrimaryLocked : {}),
            }}
            onClick={handleAuthenticate}
            disabled={isLoginLocked || isAuthenticating}
          >
            {isAuthenticating
              ? "Authenticating…"
              : isLoginLocked
                ? "🔒 Account temporarily locked"
                : "Sign in securely"}
          </button>
        </div>
      </div>

      {/* OTP Modal */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div
            style={styles.modalBox}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <h2 id="modal-title" style={styles.modalTitle}>
              Two-factor verification
            </h2>
            <p style={styles.modalHint}>
              Enter the 6-digit code sent to your registered email address.
            </p>

            {authMessage && authMessageType === "warning" && isModalOpen && (
              <Alert
                type="warning"
                title="Verification failed"
                timerText={
                  isOtpLocked ? formatLockTime(otpRemainingSeconds) : null
                }
              >
                {authMessage}
              </Alert>
            )}

            <FocusInput
              style={styles.otpInput}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="——————"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={isOtpLocked}
              onKeyDown={(e) =>
                e.key === "Enter" && !isOtpLocked && handleOtpVerification()
              }
              autoFocus
            />

            <div style={styles.modalActions}>
              <button
                className="admin-btn-verify"
                style={{
                  ...styles.btnVerify,
                  ...(isOtpLocked
                    ? { opacity: 0.45, cursor: "not-allowed" }
                    : {}),
                }}
                onClick={handleOtpVerification}
                disabled={isOtpLocked || isVerifyingOtp}
              >
                {isVerifyingOtp
                  ? "Verifying…"
                  : isOtpLocked
                    ? "🔒 Verification locked"
                    : "Verify & continue"}
              </button>
              <button
                className="admin-btn-cancel"
                style={styles.btnCancel}
                onClick={() => setIsModalOpen(false)}
                disabled={isVerifyingOtp}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminLogin;
